import React, { useCallback, useEffect } from "react";
import { X, CaretLeft, CaretRight } from "@phosphor-icons/react";
import GigPhoto from "@/components/GigPhoto";

// Full-screen viewer for authenticated gig photos. Tap backdrop / X / Escape
// to close; arrows or Left/Right keys to move between photos.
export default function PhotoLightbox({ photos, index, onClose, onIndexChange }) {
  const count = photos.length;

  const prev = useCallback(
    (e) => {
      e?.stopPropagation();
      onIndexChange((index - 1 + count) % count);
    },
    [index, count, onIndexChange]
  );
  const next = useCallback(
    (e) => {
      e?.stopPropagation();
      onIndexChange((index + 1) % count);
    },
    [index, count, onIndexChange]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && count > 1) prev();
      if (e.key === "ArrowRight" && count > 1) next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, prev, next, count]);

  if (index == null || !photos[index]) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      <button
        onClick={onClose}
        aria-label="Close"
        data-testid="lightbox-close-btn"
        className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
      >
        <X size={22} weight="bold" />
      </button>

      {count > 1 && (
        <>
          <button
            onClick={prev}
            aria-label="Previous photo"
            data-testid="lightbox-prev-btn"
            className="absolute left-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
          >
            <CaretLeft size={22} weight="bold" />
          </button>
          <button
            onClick={next}
            aria-label="Next photo"
            data-testid="lightbox-next-btn"
            className="absolute right-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
          >
            <CaretRight size={22} weight="bold" />
          </button>
        </>
      )}

      <div onClick={(e) => e.stopPropagation()} className="max-h-[88vh] max-w-[94vw]">
        <GigPhoto
          key={photos[index]}
          path={photos[index]}
          testId="lightbox-photo"
          className="max-h-[88vh] max-w-[94vw] rounded object-contain"
        />
      </div>

      {count > 1 && (
        <div
          className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 font-mono text-xs text-white"
          data-testid="lightbox-counter"
        >
          {index + 1} / {count}
        </div>
      )}
    </div>
  );
}
